import { Box, Tooltip, tooltipClasses } from "@mui/material";
import { Colors } from "@analyticsMD/quil";
import type { Options } from "@popperjs/core";
import type { FC, ReactNode } from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

/** Tallest the hover box may ever get, per design (ENG-30651). */
const MAX_HEIGHT_PX = 400;
/** Gap kept between the box and the viewport edge. */
const VIEWPORT_PADDING_PX = 12;
/**
 * Space the arrow + popper offset take between the anchor and the box. MUI's
 * arrow variant uses a 14px margin; we round up so the capped height genuinely
 * fits the side flip picks — if it were even a pixel short, flip would see both
 * sides overflowing, keep `bottom`, and let preventOverflow shift the box off
 * its anchor instead of flipping above.
 */
const ARROW_OFFSET_PX = 16;
/** The box's own vertical padding (0.4rem top + 0.4rem bottom), which sits
 * outside the scrolling content wrapper. */
const VERTICAL_PADDING_PX = 13;

const tooltipSx = {
  fontSize: "0.8rem",
  [`&.${tooltipClasses.tooltip}`]: {
    width: "80%",
    backgroundColor: Colors.grey_80,
    color: Colors.white,
    borderRadius: "0.2rem !important",
    boxShadow: "none",
    paddingTop: "0.4rem",
    paddingBottom: "0.4rem",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: Colors.grey_80,
  },
};

// Prefer below the anchor, fall back above when there isn't room. Height is
// capped to whichever side has more space (see computeContentMaxHeightPx), so
// the box always fits on the side flip chooses instead of being clipped.
const popperModifiers: NonNullable<Options["modifiers"]> = [
  {
    name: "flip",
    enabled: true,
    options: {
      padding: VIEWPORT_PADDING_PX,
      fallbackPlacements: ["top"],
    },
  },
  {
    name: "preventOverflow",
    enabled: true,
    options: {
      boundary: "viewport",
      padding: VIEWPORT_PADDING_PX,
      altAxis: true,
    },
  },
];

/**
 * Max height for the scrolling content, derived from the room around the
 * anchor. We use `max(above, below)` because flip picks whichever side has
 * more space — at this height the box is guaranteed to fit on that side, so it
 * scrolls internally rather than overflowing the viewport.
 *
 * The available room always wins over the design max: a box taller than its
 * side would hide text off-screen that the user cannot scroll to, since the
 * box is positioned relative to the viewport.
 */
function computeContentMaxHeightPx(anchor: HTMLElement): number {
  const rect = anchor.getBoundingClientRect();
  const above = rect.top - ARROW_OFFSET_PX - VIEWPORT_PADDING_PX;
  const below =
    window.innerHeight - rect.bottom - ARROW_OFFSET_PX - VIEWPORT_PADDING_PX;
  const room = Math.floor(Math.max(above, below)) - VERTICAL_PADDING_PX;
  return Math.max(0, Math.min(MAX_HEIGHT_PX - VERTICAL_PADDING_PX, room));
}

interface ConsiderationHoverBoxProps {
  /** Reasoning content shown inside the box. */
  title: ReactNode;
  /**
   * The hover target (the consideration text). Rendered inside this
   * component's own wrapper `div`, which is the anchor Popper measures — pass
   * the bare text/Typography rather than pre-wrapping it, so the markup stays
   * a single block-level wrapper and the row's layout is unchanged.
   */
  children: ReactNode;
}

/**
 * Hover box for a consideration's reasoning. Opens below the anchor, flips
 * above when space is tight, and caps its height to the space actually
 * available so it never gets clipped by the viewport (ENG-30651).
 */
const ConsiderationHoverBox: FC<ConsiderationHoverBoxProps> = ({
  title,
  children,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [contentMaxHeightPx, setContentMaxHeightPx] = useState(MAX_HEIGHT_PX);

  const measure = useCallback(() => {
    const anchor = anchorRef.current;
    if (anchor) {
      const next = computeContentMaxHeightPx(anchor);
      // Bail out when the height is unchanged so we don't re-render for nothing.
      setContentMaxHeightPx((prev) => (prev === next ? prev : next));
    }
  }, []);

  // Coalesce scroll/resize bursts into one measurement per frame. The open-time
  // measurement stays synchronous (see handleOpen) — deferring that one would
  // let the popper mount at the wrong size first.
  const scheduleMeasure = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      measure();
    });
  }, [measure]);

  // Measure before the popper mounts so popper.js sees the constrained size on
  // its first pass. Doing this in a popper modifier instead would feed the
  // resize back into popper and make the placement flicker.
  const handleOpen = useCallback(() => {
    measure();
    setOpen(true);
  }, [measure]);

  // Keep the cap honest while the box is open (window resize, page scroll).
  useLayoutEffect(() => {
    if (!open) return undefined;
    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("scroll", scheduleMeasure, true);
    return () => {
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("scroll", scheduleMeasure, true);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [open, scheduleMeasure]);

  return (
    <Tooltip
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      title={
        <Box sx={{ maxHeight: `${contentMaxHeightPx}px`, overflowY: "auto" }}>
          {title}
        </Box>
      }
      placement="bottom"
      arrow
      slotProps={{
        tooltip: { sx: tooltipSx },
        popper: { modifiers: popperModifiers },
      }}
    >
      <Box ref={anchorRef}>{children}</Box>
    </Tooltip>
  );
};

export default ConsiderationHoverBox;
